'use client';
/**
 * REVUE DE CONTRAT & ÉVALUATIONS — ce qu'on JUGE des tiers de l'ANNUAIRE (L2, 10/10/2026 ; l'annuaire a remplacé le
 * carnet de contacts le 20/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Une rubrique revue de contrat et évaluations qui tire les infos du carnet
 * et y rajoute les évaluations et la revue. » — RQ, 10/10/2026. Trois choses
 * par tiers, et rien d'autre : le CONTRAT (objet, dates, périodicité
 * OBLIGATOIRE), les REVUES consignées, les ÉVALUATIONS notées. L'identité et
 * les coordonnées restent au carnet ; cet écran ne les édite pas.
 *
 * ── Ce que l'écran refuse de faire ────────────────────────────────────────
 *  · SAISIR UNE PROCHAINE DATE. Elle se calcule (dernière revue + périodicité)
 *    et s'affiche avec son origine — une échéance sans origine ne se discute pas.
 *  · PROPOSER UNE GRILLE À UNE CLINIQUE. Seuls laboratoires et fournisseurs
 *    s'évaluent ; les autres ont un contrat à revoir, pas une note à recevoir.
 *  · LAISSER UN BOUTON QUI ÉCHOUERA : « Contrat », « Revue », « Évaluer »
 *    n'apparaissent que sur les lignes que le serveur dit modifiables.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed, userName } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { I, card, inputStyle, EmptyState, FilterBar, FieldError, useBusy, Selecteur, RefISO } from '@/components/ui';
import { lbl, pill } from './ressources/commun';
import { EnteteEcran } from '@/components/EnteteEcran';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { Segment } from '@/shared/Segment';
import { BoutonCreer } from '@/components/ui';
import { ChoixCorrespondant } from '@/components/ChoixCorrespondant';

type Critere = { key: string; label: string; aide?: string };
type Revue = { id: string; date: string; par: string; verdict: string; commentaire: string };
type Evaluation = { id: string; date: string; par: string; scores: Record<string, number>; note: number; verdict: string; commentaire: string };
type Accreditation = { statut: string; organisme: string; numero: string; porteeResume: string; valideJusqua: string };
type Ligne = {
  contactCode: string; nom: string; types: string[]; familles: string[]; actif: boolean; existe: boolean;
  objet: string; reference: string; dateDebut: string; dateFin: string; periodiciteMois: number | null;
  derniereRevue: string;
  prochaine: { date: string; etat: string; base: 'derniere_revue' | 'debut_contrat' | null; jours: number | null };
  revues: Revue[]; evaluations: Evaluation[]; derniereEvaluation: Evaluation | null;
  accreditation: Accreditation | null; justificationChoix: string; note: string;
  sEvalue: boolean; aContrat: boolean; manques: string[]; modifiable: boolean;
};
type Registre = {
  lignes: Ligne[];
  orphelins: { contactCode: string; contactNom: string }[];
  catalogues: {
    familles: { code: string; label: string }[];
    periodicites: { mois: number; label: string }[];
    verdictsRevue: { code: string; label: string }[];
    verdictsNote: Record<string, string>;
    etats: Record<string, string>;
    criteresParFamille: Record<string, Critere[]>;
    bornes: { noteMaxCritere: number; seuilSuspendu: number; seuilSurveillance: number };
  };
  compte: { total: number; avecContrat: number; enRetard: number; aPlanifier: number; evaluesSansContrat: number; contactsSansDossier: number };
  aujourdhui: string;
  peutEcrire: boolean;
};

const encart: React.CSSProperties = {
  background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)',
  borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5,
};
const TEINTE_ETAT: Record<string, { bg: string; fg: string }> = {
  a_jour: { bg: 'var(--green-50)', fg: 'var(--green-700)' },
  a_planifier: { bg: 'var(--amber-50)', fg: 'var(--amber-700)' },
  en_retard: { bg: 'var(--red-50)', fg: 'var(--red-600)' },
  jamais: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  sans_periodicite: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
};
const TEINTE_VERDICT: Record<string, { bg: string; fg: string }> = {
  qualifie: { bg: 'var(--green-50)', fg: 'var(--green-700)' },
  surveillance: { bg: 'var(--amber-50)', fg: 'var(--amber-700)' },
  suspendu: { bg: 'var(--red-50)', fg: 'var(--red-600)' },
};

/** Le panneau vient sous les yeux à l'ouverture — la ligne cliquée est plus bas. */
function useAmenerAlEcran<T>(cle: T) {
  const racine = useRef<HTMLDivElement>(null);
  useEffect(() => { racine.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }, [cle]);
  return racine;
}

async function ecrire(chemin: string, corps: unknown): Promise<{ ok: boolean; message: string }> {
  const r = await fetchEcriture(chemin, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
  });
  const j = await r.json().catch(() => null);
  return { ok: r.ok, message: j?.error?.message || '' };
}

/* ───────────────────────────── Le contrat ───────────────────────────── */

function ContratForm({ l, cat, onFait, onAnnule }: { l: Ligne; cat: Registre['catalogues']; onFait: () => void; onAnnule: () => void }) {
  const { showToast } = useGed();
  const racine = useAmenerAlEcran(l.contactCode);
  const estLabo = l.familles.includes('laboratoires');
  const [f, setF] = useState({
    objet: l.objet, reference: l.reference, dateDebut: l.dateDebut, dateFin: l.dateFin,
    periodiciteMois: l.periodiciteMois ? String(l.periodiciteMois) : '',
    justificationChoix: l.justificationChoix, note: l.note,
    accreditation: l.accreditation || { statut: 'inconnu', organisme: '', numero: '', porteeResume: '', valideJusqua: '' },
  });
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const enregistrer = () => run('save', async () => {
    setErreur('');
    const r = await ecrire(`/api/ged/ged/revues-tiers/${encodeURIComponent(l.contactCode)}`, {
      ...f, periodiciteMois: Number(f.periodiciteMois), accreditation: estLabo ? f.accreditation : null,
    });
    if (!r.ok) { setErreur(r.message || 'Le contrat n’a pas pu être enregistré.'); return; }
    showToast('Contrat enregistré.'); onFait();
  });
  const acc = f.accreditation;
  return (
    <div ref={racine} style={{ ...card, padding: 18, display: 'grid', gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>Contrat — {l.nom}</h2>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="ct-objet">Objet du contrat ou de la convention</label>
          <input id="ct-objet" value={f.objet} onChange={(e) => setF({ ...f, objet: e.target.value })} style={inputStyle}
            placeholder={estLabo ? 'Ex. examens confiés : sérologies, biologie moléculaire' : 'Ex. réactifs de biochimie, maintenance des automates'} /></div>
        <div><label style={lbl} htmlFor="ct-ref">Référence</label>
          <input id="ct-ref" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="ct-per">Périodicité de revue *</label>
          <Selecteur id="ct-per" value={f.periodiciteMois} onChange={(v) => setF({ ...f, periodiciteMois: v })} style={inputStyle}>
            <option value="">— obligatoire —</option>
            {cat.periodicites.map((p) => <option key={p.mois} value={String(p.mois)}>{p.label}</option>)}
          </Selecteur></div>
        <div><label style={lbl} htmlFor="ct-du">Début du contrat</label>
          <input id="ct-du" type="date" value={f.dateDebut} onChange={(e) => setF({ ...f, dateDebut: e.target.value })} style={inputStyle} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Point de départ de la première revue, tant qu’aucune n’est consignée.</div></div>
        <div><label style={lbl} htmlFor="ct-au">Fin du contrat (si à durée déterminée)</label>
          <input id="ct-au" type="date" value={f.dateFin} onChange={(e) => setF({ ...f, dateFin: e.target.value })} style={inputStyle} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="ct-just">Pourquoi ce tiers plutôt qu’un autre</label>
          <textarea id="ct-just" value={f.justificationChoix} rows={2} onChange={(e) => setF({ ...f, justificationChoix: e.target.value })}
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} /></div>
        {estLabo && (<>
          <div style={{ gridColumn: '1 / -1', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginTop: 4 }}>
            Accréditation (<RefISO code="6.7" />) — une portée qui ne couvre pas l’examen confié ne vaut pas pour cet examen
          </div>
          <div><label style={lbl} htmlFor="ct-acc">Accrédité</label>
            <Selecteur id="ct-acc" value={acc.statut} onChange={(v) => setF({ ...f, accreditation: { ...acc, statut: v } })} style={inputStyle}>
              <option value="inconnu">Inconnu — à vérifier</option><option value="oui">Oui</option><option value="non">Non</option>
            </Selecteur></div>
          <div><label style={lbl} htmlFor="ct-org">Organisme et numéro</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id="ct-org" value={acc.organisme} placeholder="TUNAC, COFRAC…" onChange={(e) => setF({ ...f, accreditation: { ...acc, organisme: e.target.value } })} style={inputStyle} />
              <input aria-label="Numéro d’accréditation" value={acc.numero} onChange={(e) => setF({ ...f, accreditation: { ...acc, numero: e.target.value } })} style={inputStyle} />
            </div></div>
          <div><label style={lbl} htmlFor="ct-portee">Portée (résumé)</label>
            <input id="ct-portee" value={acc.porteeResume} onChange={(e) => setF({ ...f, accreditation: { ...acc, porteeResume: e.target.value } })} style={inputStyle} /></div>
          <div><label style={lbl} htmlFor="ct-valide">Valide jusqu’au</label>
            <input id="ct-valide" type="date" value={acc.valideJusqua} onChange={(e) => setF({ ...f, accreditation: { ...acc, valideJusqua: e.target.value } })} style={inputStyle} /></div>
        </>)}
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="ct-note">Note</label>
          <textarea id="ct-note" value={f.note} rows={2} onChange={(e) => setF({ ...f, note: e.target.value })}
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} /></div>
      </div>
      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }}
        principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

/* ───────────────────────────── La revue ───────────────────────────── */

function RevueForm({ l, cat, aujourdhui, onFait, onAnnule }: { l: Ligne; cat: Registre['catalogues']; aujourdhui: string; onFait: () => void; onAnnule: () => void }) {
  const { showToast } = useGed();
  const racine = useAmenerAlEcran(l.contactCode);
  const [f, setF] = useState({ date: aujourdhui, verdict: 'maintenu', commentaire: '' });
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const consigner = () => run('save', async () => {
    setErreur('');
    const r = await ecrire(`/api/ged/ged/revues-tiers/${encodeURIComponent(l.contactCode)}/revues`, f);
    if (!r.ok) { setErreur(r.message || 'La revue n’a pas pu être consignée.'); return; }
    showToast('Revue consignée — la prochaine est recalculée.'); onFait();
  });
  return (
    <div ref={racine} style={{ ...card, padding: 18, display: 'grid', gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>Revue du contrat — {l.nom}</h2>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div><label style={lbl} htmlFor="rv-date">Date de la revue</label>
          <input id="rv-date" type="date" value={f.date} max={aujourdhui} onChange={(e) => setF({ ...f, date: e.target.value })} style={inputStyle} /></div>
        <div><label style={lbl} htmlFor="rv-verdict">Conclusion</label>
          <Selecteur id="rv-verdict" value={f.verdict} onChange={(v) => setF({ ...f, verdict: v })} style={inputStyle}>
            {cat.verdictsRevue.map((v) => <option key={v.code} value={v.code}>{v.label}</option>)}
          </Selecteur></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="rv-com">Ce qui a été vu{f.verdict !== 'maintenu' ? ' *' : ''}</label>
          <textarea id="rv-com" value={f.commentaire} rows={3} onChange={(e) => setF({ ...f, commentaire: e.target.value })}
            placeholder="Prix, délais, litiges de l’année, conformité des livraisons…"
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} /></div>
      </div>
      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }}
        principale={{ libelle: 'Consigner la revue', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void consigner() }} />
    </div>
  );
}

/* ───────────────────────────── L'évaluation ───────────────────────────── */

function EvalForm({ l, cat, aujourdhui, onFait, onAnnule }: { l: Ligne; cat: Registre['catalogues']; aujourdhui: string; onFait: () => void; onAnnule: () => void }) {
  const { showToast } = useGed();
  const racine = useAmenerAlEcran(l.contactCode);
  const criteres = l.familles.includes('laboratoires') ? cat.criteresParFamille.laboratoires : cat.criteresParFamille.fournisseurs;
  const max = cat.bornes.noteMaxCritere;
  const [scores, setScores] = useState<Record<string, number>>(() => Object.fromEntries((criteres || []).map((c) => [c.key, 15])));
  const [f, setF] = useState({ date: aujourdhui, commentaire: '' });
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const note = criteres?.length ? Math.round(((criteres.reduce((s, c) => s + (scores[c.key] || 0), 0)) / (criteres.length * max)) * 100) : 0;
  const verdict = note < cat.bornes.seuilSuspendu ? 'suspendu' : note < cat.bornes.seuilSurveillance ? 'surveillance' : 'qualifie';
  const evaluer = () => run('save', async () => {
    setErreur('');
    const r = await ecrire(`/api/ged/ged/revues-tiers/${encodeURIComponent(l.contactCode)}/evaluations`, { ...f, scores });
    if (!r.ok) { setErreur(r.message || 'L’évaluation n’a pas pu être enregistrée.'); return; }
    showToast(`Évaluation enregistrée — ${note}/100, ${cat.verdictsNote[verdict]}.`); onFait();
  });
  return (
    <div ref={racine} style={{ ...card, padding: 18, display: 'grid', gap: 12 }}>
      <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>Évaluation — {l.nom}</h2>
      <div style={{ display: 'grid', gap: 8 }}>
        {(criteres || []).map((c) => (
          <div key={c.key} style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: 10, alignItems: 'center' }}>
            <div>
              <label style={{ ...lbl, marginBottom: 0 }} htmlFor={`ev-${c.key}`}>{c.label}</label>
              {c.aide && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.aide}</div>}
            </div>
            <input id={`ev-${c.key}`} type="number" min={0} max={max} value={scores[c.key] ?? 0}
              onChange={(e) => setScores({ ...scores, [c.key]: Math.max(0, Math.min(max, Number(e.target.value))) })}
              style={{ ...inputStyle, textAlign: 'end' }} aria-describedby={`ev-${c.key}-sur`} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
        <strong style={{ fontSize: 15, color: 'var(--text-strong)' }}>{note}/100</strong>
        {pill({ label: cat.verdictsNote[verdict], ...TEINTE_VERDICT[verdict] })}
        <span style={{ color: 'var(--text-muted)' }}>chaque critère sur {max} · suspendu sous {cat.bornes.seuilSuspendu}, surveillance sous {cat.bornes.seuilSurveillance}</span>
      </div>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div><label style={lbl} htmlFor="ev-date">Date</label>
          <input id="ev-date" type="date" value={f.date} max={aujourdhui} onChange={(e) => setF({ ...f, date: e.target.value })} style={inputStyle} /></div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="ev-com">Commentaire{verdict !== 'qualifie' ? ' *' : ''}</label>
          <textarea id="ev-com" value={f.commentaire} rows={2} onChange={(e) => setF({ ...f, commentaire: e.target.value })}
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} /></div>
      </div>
      <FieldError>{erreur}</FieldError>
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }}
        principale={{ libelle: 'Enregistrer l’évaluation', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void evaluer() }} />
    </div>
  );
}

/* ───────────────────────────── L'écran ───────────────────────────── */

export function RevueContrats() {
  const { settings } = useGed();
  const [d, setD] = useState<Registre | null>(null);
  const [chargement, setChargement] = useState(true);
  const [echec, setEchec] = useState(false);
  const [tour, setTour] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [famille, setFamille] = useState('toutes');
  const [panneau, setPanneau] = useState<{ quoi: 'contrat' | 'revue' | 'eval'; code: string } | null>(null);
  /* « Nouveau contrat » : on choisit D'ABORD le tiers dans l'annuaire, puis le
     formulaire s'ouvre pour lui. Seuls les tiers sans dossier sont proposés. */
  const [nouveau, setNouveau] = useState<string | null>(null);
  const [historique, setHistorique] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/revues-tiers', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) { setD(j); setEchec(!j); } })
      .catch(() => { if (vivant) { setD(null); setEchec(true); } })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);
  const recharger = () => { setPanneau(null); setTour((t) => t + 1); };

  const filtrees = useMemo(() => {
    /* ⚠️ PAS DE CONTRAT, PAS DE REVUE (RQ, 10/10/2026) : la liste ne montre que
       les tiers qui ont un dossier — contrat ou évaluation. Un correspondant
       occasionnel n'y est pas, et ce n'est pas un manque. */
    const base = (d?.lignes || []).filter((l) => l.actif && l.existe)
      .filter((l) => famille === 'toutes' || l.familles.includes(famille))
      .filter((l) => (
        filtre === 'retard' ? l.prochaine.etat === 'en_retard'
          : filtre === 'planifier' ? l.prochaine.etat === 'a_planifier'
            : filtre === 'sans' ? !l.aContrat
              : filtre === 'ajour' ? l.prochaine.etat === 'a_jour'
                : true));
    const qq = q.trim().toLowerCase();
    const avecQ = qq ? base.filter((l) => `${l.nom} ${l.contactCode} ${l.objet} ${l.reference}`.toLowerCase().includes(qq)) : base;
    /* Le retard d'abord : c'est ce qu'on vient regarder. */
    const rang: Record<string, number> = { en_retard: 0, a_planifier: 1, jamais: 2, sans_periodicite: 2, a_jour: 3 };
    return avecQ.sort((a, b) => (rang[a.prochaine.etat] ?? 9) - (rang[b.prochaine.etat] ?? 9) || a.nom.localeCompare(b.nom));
  }, [d, filtre, famille, q]);

  if (chargement) return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement des contrats et évaluations…</p>;
  if (echec || !d) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <EnteteEcran rubrique="Parc, métrologie & achats" clauseISO="6.8" titre="Revue de contrat & évaluations" />
        <div style={encart}>Le registre n’a pas pu être chargé. Ce n’est <strong>pas</strong> « aucun contrat » : l’écran ne sait rien pour l’instant.</div>
      </div>
    );
  }
  const { catalogues: cat, compte } = d;
  const ligneDe = (code: string) => d.lignes.find((l) => l.contactCode === code) || null;
  const ouverte = panneau ? ligneDe(panneau.code) : null;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <EnteteEcran rubrique="Parc, métrologie & achats" clauseISO="6.8" titre="Revue de contrat & évaluations"
        actions={d.peutEcrire && nouveau === null && !panneau
          ? <BoutonCreer onClick={() => setNouveau('')}>Nouveau contrat</BoutonCreer> : undefined} />
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Ce qu’on <strong>juge</strong> des tiers de l’annuaire qui ont un <strong>contrat</strong> ou
        une convention : sa revue périodique (obligatoire dès qu’il y a contrat), les évaluations notées, et pour
        un laboratoire son accréditation et sa portée. La prochaine revue se calcule — dernière revue +
        périodicité — et n’est jamais saisie. Un correspondant occasionnel n’a ni contrat ni revue : il n’apparaît
        pas ici. L’identité et les coordonnées restent dans l’annuaire.
      </p>

      {nouveau !== null && !panneau && (
        <div style={{ ...card, padding: 18, display: 'grid', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>Nouveau contrat — pour quel tiers ?</h2>
          <ChoixCorrespondant id="nv-contact" valeur={nouveau} onChange={setNouveau}
            exclure={d.lignes.filter((l) => l.existe).map((l) => l.contactCode)}
            aide="Les tiers déjà suivis ne sont pas proposés. Un tiers absent se crée d’abord dans l’annuaire." />
          <PiedActions taille="sm" annuler={{ onClick: () => setNouveau(null) }}
            principale={{ libelle: 'Décrire le contrat', disabled: !nouveau, onClick: () => { setPanneau({ quoi: 'contrat', code: nouveau }); setNouveau(null); } }} />
        </div>
      )}

      {compte.enRetard > 0 && (
        <div style={encart}><strong style={{ color: 'var(--text-strong)' }}>{compte.enRetard} revue{compte.enRetard > 1 ? 's' : ''} de contrat en retard.</strong>{' '}
          {compte.aPlanifier > 0 && `${compte.aPlanifier} à planifier dans les soixante jours.`}</div>
      )}
      {compte.total === 0 && (
        <EmptyState titre="Aucun contrat enregistré."
          indice={`${compte.contactsSansDossier} tiers dans l’annuaire, et c’est normal qu’ils n’aient pas tous un contrat. Commencez par ceux qui en ont un : le fournisseur de réactifs principal, le laboratoire sous-traitant, les cliniques conventionnées.`} />
      )}
      {d.orphelins.length > 0 && (
        <div style={encart}>{d.orphelins.length} dossier(s) dont le tiers n’est plus dans l’annuaire ({d.orphelins.map((o) => o.contactNom || o.contactCode).join(', ')}) : conservés, non affichés.</div>
      )}

      {ouverte && panneau?.quoi === 'contrat' && <ContratForm key={`c-${ouverte.contactCode}`} l={ouverte} cat={cat} onFait={recharger} onAnnule={() => setPanneau(null)} />}
      {ouverte && panneau?.quoi === 'revue' && <RevueForm key={`r-${ouverte.contactCode}`} l={ouverte} cat={cat} aujourdhui={d.aujourdhui} onFait={recharger} onAnnule={() => setPanneau(null)} />}
      {ouverte && panneau?.quoi === 'eval' && <EvalForm key={`e-${ouverte.contactCode}`} l={ouverte} cat={cat} aujourdhui={d.aujourdhui} onFait={recharger} onAnnule={() => setPanneau(null)} />}

      {compte.total > 0 && (<>
      <Segment ariaLabel="Famille de tiers" valeur={famille} onChange={setFamille}
        options={[
          { valeur: 'toutes', libelle: 'Tous', compteur: compte.total },
          ...cat.familles.map((fa) => ({ valeur: fa.code, libelle: fa.label, compteur: d.lignes.filter((l) => l.actif && l.existe && l.familles.includes(fa.code)).length })),
        ]} />
      <FilterBar q={q} onQ={setQ} placeholder="Rechercher un tiers, un contrat…"
        chips={[
          { k: 'tous', label: 'Tous', n: compte.total },
          { k: 'retard', label: 'En retard', n: compte.enRetard },
          { k: 'planifier', label: 'À planifier', n: compte.aPlanifier },
          { k: 'ajour', label: 'À jour', n: d.lignes.filter((l) => l.actif && l.prochaine.etat === 'a_jour').length },
          { k: 'sans', label: 'Évalués sans contrat', n: compte.evaluesSansContrat },
        ]}
        actif={filtre} onChip={setFiltre} total={compte.total} affiches={filtrees.length}
        onReset={() => { setQ(''); setFiltre('tous'); setFamille('toutes'); }} />
      {filtrees.length === 0 && <EmptyState titre="Aucun contrat ne correspond." />}
      </>)}

      <div style={{ display: 'grid', gap: 10 }}>
        {filtrees.map((l) => {
          const etat = l.prochaine.etat;
          const ev = l.derniereEvaluation;
          return (
            <div key={l.contactCode} style={{ ...card, padding: 14, display: 'grid', gap: 8 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 14, color: 'var(--text-strong)' }}>{l.nom}</strong>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)' }}>{l.contactCode}</span>
                {l.aContrat
                  ? pill({ label: cat.etats[etat] || etat, ...(TEINTE_ETAT[etat] || TEINTE_ETAT.jamais) })
                  : pill({ label: 'Sans contrat — évalué ponctuellement', ...TEINTE_ETAT.jamais })}
                {ev && pill({ label: `${ev.note}/100 · ${cat.verdictsNote[ev.verdict] || ev.verdict}`, ...(TEINTE_VERDICT[ev.verdict] || TEINTE_VERDICT.qualifie) })}
                <span style={{ flex: 1 }} />
                {l.modifiable && (<>
                  <Bouton taille="sm" onClick={() => setPanneau({ quoi: 'contrat', code: l.contactCode })}><I n="pen" s={12} />{l.aContrat ? 'Contrat' : 'Enregistrer un contrat'}</Bouton>
                  {l.aContrat && <Bouton taille="sm" onClick={() => setPanneau({ quoi: 'revue', code: l.contactCode })}><I n="check" s={12} />Consigner une revue</Bouton>}
                  {l.sEvalue && <Bouton taille="sm" onClick={() => setPanneau({ quoi: 'eval', code: l.contactCode })}><I n="scale" s={12} />Évaluer</Bouton>}
                </>)}
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-body)' }}>
                {l.objet ? <span>{l.objet}{l.reference ? ` · réf. ${l.reference}` : ''}</span> : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>aucun contrat décrit</span>}
                {l.periodiciteMois && <span>Revue tous les <strong>{l.periodiciteMois} mois</strong></span>}
                {l.dateDebut && <span>du {fmtDate(l.dateDebut)}{l.dateFin ? ` au ${fmtDate(l.dateFin)}` : ''}</span>}
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text-muted)' }}>
                {!l.aContrat ? null : l.prochaine.date
                  ? <span>Prochaine revue le <strong style={{ color: etat === 'en_retard' ? 'var(--red-600)' : 'var(--text-strong)' }}>{fmtDate(l.prochaine.date)}</strong>
                    {' '}({l.prochaine.base === 'derniere_revue' ? `dernière revue le ${fmtDate(l.derniereRevue)}` : `début du contrat le ${fmtDate(l.dateDebut)}`}{l.prochaine.jours !== null && l.prochaine.jours < 0 ? `, ${-l.prochaine.jours} j de retard` : ''})</span>
                  : <span>Prochaine revue : {cat.etats[etat]}</span>}
                {l.accreditation && <span>Accréditation : <strong>{l.accreditation.statut}</strong>{l.accreditation.organisme ? ` · ${l.accreditation.organisme}` : ''}{l.accreditation.valideJusqua ? ` · jusqu’au ${fmtDate(l.accreditation.valideJusqua)}` : ''}</span>}
                {(l.revues.length > 0 || l.evaluations.length > 0) && (
                  <Bouton taille="sm" variante="discret" onClick={() => setHistorique(historique === l.contactCode ? null : l.contactCode)}>
                    {historique === l.contactCode ? 'Replier l’historique' : `Historique (${l.revues.length} revue${l.revues.length > 1 ? 's' : ''}, ${l.evaluations.length} évaluation${l.evaluations.length > 1 ? 's' : ''})`}
                  </Bouton>
                )}
              </div>
              {l.manques.length > 0 && l.aContrat && <div style={{ fontSize: 12.5, color: 'var(--amber-700)' }}>À compléter : {l.manques.join(' · ')}.</div>}
              {historique === l.contactCode && (
                <div style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--border-subtle)', paddingTop: 8, fontSize: 12.5 }}>
                  {[...l.revues].reverse().map((r) => (
                    <div key={r.id}><strong>{fmtDate(r.date)}</strong> — revue : {cat.verdictsRevue.find((v) => v.code === r.verdict)?.label || r.verdict}{r.commentaire ? ` — ${r.commentaire}` : ''} <span style={{ color: 'var(--text-muted)' }}>({userName(settings, r.par)})</span></div>
                  ))}
                  {[...l.evaluations].reverse().map((e) => (
                    <div key={e.id}><strong>{fmtDate(e.date)}</strong> — évaluation : {e.note}/100, {cat.verdictsNote[e.verdict] || e.verdict}{e.commentaire ? ` — ${e.commentaire}` : ''} <span style={{ color: 'var(--text-muted)' }}>({userName(settings, e.par)})</span></div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
